import React, { useState, useEffect } from 'react';
import bgimage from './assets/images/login.jpg';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { completeLogin as sessionCompleteLogin, hashQuery } from './auth/session';
import './Login.css';

function Login() {
  const { t, i18n } = useTranslation();
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState('credentials');   // 'credentials' | 'mfa' | 'forgot' | 'sso' | 'support'
  const [mfaToken, setMfaToken] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoMsg, setSsoMsg] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportMsg, setSupportMsg] = useState('');
  const [supportTicket, setSupportTicket] = useState('');
  const [supportReqId, setSupportReqId] = useState('');
  const navigate = useNavigate();

  // Surface a failed SSO round-trip (backend/SsoCallback redirect to /login?sso_error=...).
  useEffect(() => {
    const err = hashQuery().get('sso_error');
    if (err) setError(t('auth.ssoFailed', 'Single sign-on failed. Please try again or use your password.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared post-authentication flow (store token, load user, route by role) — same for SSO.
  const completeLogin = async (token) => {
    try {
      await sessionCompleteLogin({ token, userName, navigate, i18n });
    } catch (userError) {
      setError(t('auth.fetchUserError'));
    }
  };

  // Email-first SSO: ask the backend whether this email's domain federates; if so, hand the
  // browser off to the tenant's IdP. Otherwise fall back to username/password.
  const handleSsoContinue = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSsoMsg('');
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/sso/discover`, {
        params: { email: ssoEmail.trim() },
      });
      if (data && data.ssoEnabled && data.authorizeUrl) {
        const origin = new URL(process.env.REACT_APP_API_BASE_URL).origin;
        window.location.href = origin + data.authorizeUrl;   // full-page redirect to the IdP
        return;
      }
      setSsoMsg(t('auth.ssoNotConfigured', 'No SSO is set up for that email domain. Use your username and password.'));
    } catch {
      setSsoMsg(t('auth.ssoStartError', 'Could not start SSO. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/login`,
        { userName, password },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const data = response.data;
      if (data && data.mfaRequired) {
        setMfaToken(data.mfaToken);
        setStep('mfa');
        return;
      }
      await completeLogin(data.token);
    } catch (err) {
      setError(err.response?.status === 401 ? t('auth.invalidCreds') : t('auth.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/login/verify-mfa`,
        { mfaToken, code: code.trim() },
        { headers: { 'Content-Type': 'application/json' } },
      );
      await completeLogin(response.data.token);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 && err.response?.data && String(err.response.data).includes('expired')) {
        setError(t('auth.mfaExpired'));
        setStep('credentials');
        setMfaToken('');
        setCode('');
      } else if (status === 429) {
        setError(typeof err.response?.data === 'string' ? err.response.data : t('auth.mfaLocked'));
      } else {
        setError(t('auth.invalidCode'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backToLogin = () => {
    setStep('credentials');
    setMfaToken('');
    setCode('');
    setError('');
    setForgotSent(false);
    setSsoEmail('');
    setSsoMsg('');
    setSupportEmail('');
    setSupportMsg('');
    setSupportTicket('');
    setSupportReqId('');
  };

  const openSupport = () => {
    setStep('support');
    setError('');
    setSupportTicket('');
    // Idempotency key for this form session — a double-click or network retry won't duplicate the ticket.
    setSupportReqId(window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  };

  const supportEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supportEmail.trim());

  const handleSupport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/public/support-ticket`,
        { email: supportEmail.trim(), message: supportMsg.trim(), clientRequestId: supportReqId },
        { headers: { 'Content-Type': 'application/json' } },
      );
      setSupportTicket(data?.ticketNumber || 'OK');
    } catch (err) {
      setError(err.response?.status === 429
        ? t('auth.supportThrottled', 'Too many requests — please try again in a few minutes.')
        : t('auth.supportError', 'Could not send your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/forgot-password`,
        { userName },
        { headers: { 'Content-Type': 'application/json' } },
      );
    } catch {
      /* always show the same confirmation — no account enumeration */
    } finally {
      setForgotSent(true);
      setSubmitting(false);
    }
  };

  const linkBtn = { background: 'none', border: 'none', color: '#007BFF', cursor: 'pointer', padding: 0 };

  return (
    <div className="login-page" style={{ backgroundImage: `url(${bgimage})` }}>
      <div className="login-card">
        {step === 'credentials' && (
          <form className="login-form" onSubmit={handleSubmit}>
            <h2>{t('auth.loginTitle')}</h2>
            {error && <p className="error-message">{error}</p>}

            <label>{t('auth.username')}</label>
            <input type="text" value={userName} onChange={(e) => setUserName(e.target.value)}
              required placeholder={t('auth.usernamePh')} />

            <label>{t('auth.password')}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required placeholder={t('auth.passwordPh')} />

            <button type="submit" disabled={submitting}>{submitting ? t('auth.signingIn') : t('auth.login')}</button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.85rem 0', color: '#9aa0a6', fontSize: '0.8rem' }}>
              <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />{t('auth.or', 'or')}<span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>
            <button type="button" onClick={() => { setStep('sso'); setError(''); setSsoMsg(''); }}
              style={{ background: '#fff', color: '#1e293b', border: '1px solid #cbd5e1' }}>
              {t('auth.ssoLogin', 'Sign in with company SSO')}
            </button>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={() => { setStep('forgot'); setError(''); }} style={linkBtn}>
                {t('auth.forgot')}
              </button>
              <button type="button" onClick={openSupport} style={linkBtn}>
                {t('auth.needHelp', 'Need help?')}
              </button>
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#555' }}>
              {t('auth.noAccount')}{' '}
              <Link to="/register" style={{ color: '#007BFF', textDecoration: 'none' }}>
                {t('auth.startTrial')}
              </Link>
            </p>
          </form>
        )}

        {step === 'sso' && (
          <form className="login-form" onSubmit={handleSsoContinue}>
            <h2>{t('auth.ssoTitle', 'Company single sign-on')}</h2>
            {ssoMsg && <p className="error-message">{ssoMsg}</p>}
            <p style={{ fontSize: '0.875rem', color: '#555', marginBottom: '0.5rem' }}>
              {t('auth.ssoPrompt', 'Enter your work email and we’ll redirect you to your company’s login.')}
            </p>
            <label>{t('auth.workEmail', 'Work email')}</label>
            <input type="email" value={ssoEmail} onChange={(e) => setSsoEmail(e.target.value)}
              required placeholder="you@yourcompany.com" autoFocus />
            <button type="submit" disabled={submitting || !ssoEmail.trim()}>
              {submitting ? t('auth.redirecting', 'Redirecting…') : t('auth.continue', 'Continue')}
            </button>
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#555' }}>
              <button type="button" onClick={backToLogin} style={linkBtn}>{t('auth.backToLogin')}</button>
            </p>
          </form>
        )}

        {step === 'forgot' && (
          <form className="login-form" onSubmit={handleForgot}>
            <h2>{t('auth.resetTitle')}</h2>
            {forgotSent ? (
              <>
                <p style={{ fontSize: '0.9rem', color: '#15803d' }}>{t('auth.resetSent')}</p>
                <button type="button" onClick={backToLogin}>{t('auth.backToLogin')}</button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.875rem', color: '#555', marginBottom: '0.5rem' }}>{t('auth.resetPrompt')}</p>
                <label>{t('auth.usernameOrEmail')}</label>
                <input type="text" value={userName} onChange={(e) => setUserName(e.target.value)}
                  required placeholder={t('auth.usernameOrEmailPh')} />
                <button type="submit" disabled={submitting || !userName.trim()}>
                  {submitting ? t('auth.sending') : t('auth.sendResetLink')}
                </button>
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#555' }}>
                  <button type="button" onClick={backToLogin} style={linkBtn}>{t('auth.backToLogin')}</button>
                </p>
              </>
            )}
          </form>
        )}

        {step === 'support' && (
          <form className="login-form" onSubmit={handleSupport}>
            <h2>{t('auth.supportTitle', 'Contact support')}</h2>
            {supportTicket ? (
              <>
                <p style={{ fontSize: '0.9rem', color: '#15803d' }}>
                  {t('auth.supportSent', 'Thanks! Your request has been received — our support team will get back to you by email.')}
                  {supportTicket !== 'OK' && <> ({t('auth.supportTicketNo', 'Ticket')} {supportTicket})</>}
                </p>
                <button type="button" onClick={backToLogin}>{t('auth.backToLogin')}</button>
              </>
            ) : (
              <>
                {error && <p className="error-message">{error}</p>}
                <p style={{ fontSize: '0.875rem', color: '#555', marginBottom: '0.5rem' }}>
                  {t('auth.supportPrompt', 'Trouble logging in or anything else blocking you? Tell us and we’ll get back to you by email.')}
                </p>
                <label>{t('auth.supportEmail', 'Email')}</label>
                <input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)}
                  required placeholder="you@yourcompany.com" autoFocus />
                <label>{t('auth.supportMessage', 'Message')}</label>
                <textarea value={supportMsg} onChange={(e) => setSupportMsg(e.target.value)} required
                  rows={4} maxLength={5000} placeholder={t('auth.supportMessagePh', 'Describe the problem you are facing…')}
                  style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
                <button type="submit" disabled={submitting || !supportEmailValid || !supportMsg.trim()}>
                  {submitting ? t('auth.sending') : t('auth.supportSend', 'Send')}
                </button>
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#555' }}>
                  <button type="button" onClick={backToLogin} style={linkBtn}>{t('auth.backToLogin')}</button>
                </p>
              </>
            )}
          </form>
        )}

        {step === 'mfa' && (
          <form className="login-form" onSubmit={handleVerifyMfa}>
            <h2>{t('auth.mfaTitle')}</h2>
            {error && <p className="error-message">{error}</p>}
            <p style={{ fontSize: '0.875rem', color: '#555', marginBottom: '0.5rem' }}>{t('auth.mfaPrompt')}</p>

            <label>{t('auth.mfaCodeLabel')}</label>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
              value={code} onChange={(e) => setCode(e.target.value)} required placeholder={t('auth.mfaCodePh')} />

            <button type="submit" disabled={submitting || !code.trim()}>
              {submitting ? t('auth.verifying') : t('auth.verify')}
            </button>
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#555' }}>
              <button type="button" onClick={backToLogin} style={linkBtn}>{t('auth.backToLogin')}</button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
